/** O pouco de roteamento que a aplicação precisa hoje.
 *
 * Três endereços são **públicos**: o link do convite, o de nova senha e o de
 * confirmação de e-mail. Todos chegam por fora — colados numa conversa ou num
 * e-mail —, não compartilham layout com a área logada e não têm navegação
 * entre si. `rotaPublica` reconhece só esses três, e nada além deles: são
 * avaliados **antes** de saber se existe sessão (`App.tsx`), e um caminho
 * autenticado reconhecido ali entregaria a tela errada a quem ainda não
 * entrou.
 *
 * `/entregas/:projetoId` é o primeiro endereço autenticado do produto — o
 * quadro de entregas aberto a partir do card do CRM (Tarefa 14) precisa de um
 * link real para levar a pessoa até lá, e não só de uma troca de estado que
 * some ao atualizar a página. Por isso `rotaDeEntregas` fica **fora** de
 * `RotaPublica`: só faz sentido depois que a área logada já decidiu que há
 * sessão.
 *
 * A árvore de rotas do TanStack passa a valer quando houver mais do que estes
 * quatro endereços. Módulo separado dos componentes porque exportar função e
 * componente do mesmo arquivo desliga o Fast Refresh.
 */

const PADRAO_DE_ENTREGAS = /^\/entregas\/([^/?#]+)\/?$/;

/** O id do projeto, se o caminho for `/entregas/:projetoId` — `null` do
 * contrário. Não decodifica o segmento: o id é um UUID, que não carrega nada
 * que precise de `decodeURIComponent`. */
export function rotaDeEntregas(caminho: string): string | null {
  const encontrado = PADRAO_DE_ENTREGAS.exec(caminho);
  return encontrado === null ? null : (encontrado[1] as string);
}

export type RotaPublica =
  | { tipo: "convite"; token: string }
  | { tipo: "nova-senha"; token: string }
  | { tipo: "verificar-email"; token: string };

const PADROES: { tipo: RotaPublica["tipo"]; regex: RegExp }[] = [
  { tipo: "convite", regex: /^\/convite\/([^/?#]+)\/?$/ },
  { tipo: "nova-senha", regex: /^\/nova-senha\/([^/?#]+)\/?$/ },
  { tipo: "verificar-email", regex: /^\/verificar-email\/([^/?#]+)\/?$/ },
];

export function rotaPublica(caminho: string): RotaPublica | null {
  for (const { tipo, regex } of PADROES) {
    const encontrado = regex.exec(caminho);
    if (encontrado === null) continue;

    try {
      return { tipo, token: decodeURIComponent(encontrado[1] as string) };
    } catch {
      // `%` solto quebra o decode. Um token malformado não é rota pública —
      // quem decide se ele vale é o servidor, e ele nem chega lá.
      return null;
    }
  }
  return null;
}

/** Mantida porque é o caso mais usado e lê melhor no `App`. */
export function tokenDoConvite(caminho: string): string | null {
  const rota = rotaPublica(caminho);
  return rota?.tipo === "convite" ? rota.token : null;
}
